import { useMemo } from 'react';
import { AlertTriangle, RefreshCw, Settings2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  MODULE_DEFINITIONS,
  useModuleSettings,
  type BusinessType,
  type ModuleCategory,
} from '@/hooks/useModuleSettings';

const BUSINESS_TYPE_OPTIONS: Array<{
  value: BusinessType;
  label: string;
  description: string;
}> = [
  { value: 'restaurant', label: 'Restaurant', description: 'Full-service dining with order preparation and seating' },
  { value: 'qsr', label: 'Quick Service', description: 'Counter service with order preparation' },
  { value: 'bar', label: 'Bar / Lounge', description: 'Beverage service with order preparation and seating' },
  { value: 'cafe', label: 'Cafe / Bakery', description: 'Cafe workflow with order preparation and seating' },
  { value: 'retail', label: 'Retail', description: 'Retail workflow with fulfillment queue' },
  { value: 'wholesale', label: 'Wholesale', description: 'Wholesale and distribution workflow' },
  { value: 'other', label: 'Other', description: 'Customizable setup for any business' },
];

const CATEGORY_LABELS: Record<ModuleCategory, string> = {
  OPERATIONS: 'Operations',
  KITCHEN: 'Preparation',
  CATALOG: 'Catalog',
  CUSTOMERS: 'Customers',
  INSIGHTS: 'Insights',
  ADMIN: 'Admin',
};

export function ModuleSettingsSection() {
  const {
    businessType,
    setBusinessType,
    planTier,
    subscriptionLoading,
    canToggleModules,
    isSuperadmin,
    modules,
    setModuleEnabled,
    resetModulesToBusinessDefaults,
    savingModuleKey,
    isResettingModules,
    savingBusinessType,
  } = useModuleSettings();

  const savingAny = savingModuleKey !== null || isResettingModules;

  const orderedModules = useMemo(() => {
    return MODULE_DEFINITIONS
      .map((def) => modules[def.key])
      .filter(Boolean);
  }, [modules]);

  const grouped = useMemo(() => {
    const result: Partial<Record<ModuleCategory, typeof orderedModules>> = {};
    for (const mod of orderedModules) {
      if (!result[mod.category]) result[mod.category] = [];
      result[mod.category]!.push(mod);
    }
    return result;
  }, [orderedModules]);

  const businessTypeMeta = BUSINESS_TYPE_OPTIONS.find((o) => o.value === businessType);
  const hasPlanWarnings = orderedModules.some((m) => !m.available);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold">Modules</h3>
          <p className="text-sm text-muted-foreground">
            Control which areas of the app are available for this organization.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs">
            Plan: {subscriptionLoading ? '...' : planTier}
          </Badge>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={resetModulesToBusinessDefaults}
            disabled={!canToggleModules || savingAny}
          >
            <RefreshCw className={cn('h-4 w-4', isResettingModules && 'animate-spin')} />
            Reset to recommended
          </Button>
        </div>
      </div>

      {/* Business Type */}
      <div className="flex items-center justify-between p-4 rounded-xl border bg-card">
        <div className="min-w-0">
          <Label htmlFor="business-type" className="font-medium">
            Business type
          </Label>
          <p className="text-xs text-muted-foreground mt-0.5">
            Used to generate recommended module defaults.
            {businessTypeMeta?.description ? ` ${businessTypeMeta.description}.` : null}
          </p>
        </div>
        <div className="w-56">
          <Select
            value={businessType}
            onValueChange={(value) => setBusinessType(value as BusinessType)}
            disabled={!canToggleModules || savingBusinessType}
          >
            <SelectTrigger id="business-type">
              <SelectValue placeholder="Select business type" />
            </SelectTrigger>
            <SelectContent>
              {BUSINESS_TYPE_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {!canToggleModules && (
        <div className="flex items-start gap-3 p-4 rounded-xl border bg-muted/30">
          <div className="mt-0.5">
            <Settings2 className="h-4 w-4 text-muted-foreground" />
          </div>
          <div>
            <p className="text-sm font-medium">Restricted</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Only the Business Owner (primary admin) can change module availability.
            </p>
          </div>
        </div>
      )}

      {isSuperadmin && hasPlanWarnings && (
        <div className="flex items-start gap-3 p-4 rounded-xl border border-amber-500/20 bg-amber-500/5">
          <AlertTriangle className="h-5 w-5 text-amber-700 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800">Plan requirements are not finalized</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Modules marked “Requires Core plan” can still be enabled by superadmin for testing.
              These overrides affect the organization.
            </p>
          </div>
        </div>
      )}

      {/* Module Toggles */}
      <div className="space-y-8">
        {(Object.keys(CATEGORY_LABELS) as ModuleCategory[])
          .filter((cat) => grouped[cat]?.length)
          .map((cat) => (
            <div key={cat} className="space-y-3">
              <div>
                <h4 className="text-sm font-semibold tracking-tight">
                  {CATEGORY_LABELS[cat]}
                </h4>
                <p className="text-xs text-muted-foreground">
                  {cat === 'KITCHEN'
                    ? 'Kitchen features can be disabled for retail/wholesale workflows.'
                    : cat === 'ADMIN'
                      ? 'Admin tools are also permission-controlled.'
                      : 'Toggle availability for this organization.'}
                </p>
              </div>

              <div className="space-y-2">
                {grouped[cat]!.map((mod) => {
                  const planRestricted = !mod.available;
                  const canOverridePlan = isSuperadmin;
                  const saving = savingAny;
                  return (
                    <div
                      key={mod.key}
                      className={cn(
                        'flex items-center justify-between p-4 rounded-xl border bg-card',
                        planRestricted && 'opacity-80'
                      )}
                    >
                      <div className="min-w-0 pr-4">
                        <Label
                          htmlFor={`module-${mod.key}`}
                          className={cn('font-medium cursor-pointer', (planRestricted && !canOverridePlan) && 'cursor-default')}
                        >
                          {mod.label}
                        </Label>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {mod.description}
                        </p>
                      </div>

                      <div className="flex items-center gap-2">
                        {planRestricted && (
                          <Badge
                            variant="outline"
                            className="text-xs gap-1 border-amber-500/30 bg-amber-500/10 text-amber-800"
                          >
                            <AlertTriangle className="h-3 w-3" />
                            {mod.lockedReason || `Requires ${mod.minPlan || 'higher'} plan`}
                          </Badge>
                        )}
                        {mod.enabled && (
                          <Badge variant="secondary" className="text-xs">
                            On
                          </Badge>
                        )}
                        <Switch
                          id={`module-${mod.key}`}
                          checked={mod.enabled}
                          onCheckedChange={(checked) => setModuleEnabled(mod.key, checked)}
                          disabled={!canToggleModules || saving || (planRestricted && !canOverridePlan)}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}

